import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { adminApi, formatINR } from "@/lib/api";
import {
    LayoutDashboard, Package, Users, Tag, LogOut, TrendingUp, DollarSign, ShoppingCart, Sparkles, Plus, Search, Trash2, Upload, Eye, EyeOff,
} from "lucide-react";
import Logo from "@/components/Logo";
import toast from "react-hot-toast";
import BrandLogo from "@/components/BrandLogo";
import Pagination from "@/components/Pagination";

const ADMIN_BRANDS_LIMIT = 60;

export default function AdminDashboard() {
    const nav = useNavigate();
    const [tab, setTab] = useState("overview");
    const [analytics, setAnalytics] = useState(null);
    const [orders, setOrders] = useState([]);
    const [brands, setBrands] = useState([]);
    const [brandsTotal, setBrandsTotal] = useState(0);
    const [brandsPage, setBrandsPage] = useState(1);
    const [users, setUsers] = useState([]);
    const [query, setQuery] = useState("");
    const [editBrand, setEditBrand] = useState(null);
    const [showImport, setShowImport] = useState(false);

    const logout = () => {
        localStorage.removeItem("rk_admin_token");
        localStorage.removeItem("rk_admin");
        nav("/admin/login");
    };

    useEffect(() => {
        if (!localStorage.getItem("rk_admin_token")) nav("/admin/login");
    }, [nav]);

    useEffect(() => {
        (async () => {
            try {
                if (tab === "overview" && !analytics) {
                    const r = await adminApi.get("/admin/analytics");
                    setAnalytics(r.data);
                }
                if (tab === "orders") {
                    const r = await adminApi.get("/admin/orders?limit=50");
                    setOrders(r.data.items);
                }
                if (tab === "brands") {
                    const r = await adminApi.get(`/admin/brands?limit=${ADMIN_BRANDS_LIMIT}&page=${brandsPage}&q=${encodeURIComponent(query)}`);
                    setBrands(r.data.items);
                    setBrandsTotal(r.data.total);
                }
                if (tab === "users") {
                    const r = await adminApi.get("/admin/users?limit=50");
                    setUsers(r.data.items);
                }
            } catch (e) {
                if (e.response?.status === 401) { logout(); }
                else { toast.error("Failed to load"); }
            }
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, query, brandsPage]);

    return (
        <div className="min-h-screen bg-[#EFECE2] grid md:grid-cols-[240px_1fr]">
            {/* Sidebar */}
            <aside className="bg-[#0B1437] text-white p-6 md:min-h-screen">
                <Link to="/" className="flex items-center gap-2 mb-8">
                    <Logo variant="light" />
                </Link>
                <p className="text-xs opacity-60 uppercase tracking-[0.2em] mb-3">Admin</p>
                <nav className="space-y-1">
                    {[
                        { k: "overview", t: "Overview", I: LayoutDashboard },
                        { k: "orders", t: "Orders", I: Package },
                        { k: "brands", t: "Brands", I: Tag },
                        { k: "users", t: "Users", I: Users },
                    ].map((item) => (
                        <button
                            key={item.k}
                            data-testid={`admin-tab-${item.k}`}
                            onClick={() => setTab(item.k)}
                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition ${
                                tab === item.k ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5"
                            }`}
                        >
                            <item.I className="w-4 h-4" /> {item.t}
                        </button>
                    ))}
                </nav>
                <button data-testid="admin-logout-btn" onClick={logout} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-white/70 hover:bg-white/5 mt-8">
                    <LogOut className="w-4 h-4" /> Logout
                </button>
            </aside>

            <main className="p-4 md:p-8 lg:p-12">
                {tab === "overview" && (
                    <div>
                        <h1 className="font-serif text-3xl md:text-4xl mb-2">Overview</h1>
                        <p className="text-sm text-[#6C6F7A] mb-6">A quick pulse on your marketplace.</p>
                        {!analytics ? <p>Loading…</p> : (
                            <>
                                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                                    <Kpi label="Revenue" value={formatINR(analytics.summary.revenue_inr)} icon={DollarSign} accent="#0B1437" />
                                    <Kpi label="Orders" value={analytics.summary.paid_orders} sub={`${analytics.summary.total_orders} total`} icon={ShoppingCart} accent="#0B1437" />
                                    <Kpi label="Users" value={analytics.summary.total_users} icon={Users} accent="#1D5C42" />
                                    <Kpi label="Brands" value={analytics.summary.total_brands} icon={Tag} accent="#B88A1A" />
                                </div>

                                <div className="grid md:grid-cols-2 gap-4">
                                    <Card title="Top brands by revenue">
                                        {analytics.top_brands.length === 0 ? <EmptyHint /> :
                                            <table className="w-full text-sm">
                                                <thead><tr className="text-left text-[11px] uppercase tracking-wider text-[#6C6F7A]"><th className="pb-2">Brand</th><th>Orders</th><th className="text-right">Revenue</th></tr></thead>
                                                <tbody>
                                                    {analytics.top_brands.map((b, i) => (
                                                        <tr key={i} className="border-t border-[#E5E0D5]">
                                                            <td className="py-2">{b.brand}</td>
                                                            <td>{b.orders}</td>
                                                            <td className="text-right font-semibold">{formatINR(b.revenue)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        }
                                    </Card>
                                    <Card title="Revenue by category">
                                        {analytics.by_category.length === 0 ? <EmptyHint /> :
                                            <div className="space-y-3">
                                                {analytics.by_category.map((c, i) => {
                                                    const max = Math.max(...analytics.by_category.map(x => x.revenue));
                                                    return (
                                                        <div key={i}>
                                                            <div className="flex justify-between text-sm"><span>{c.category}</span><span className="font-semibold">{formatINR(c.revenue)}</span></div>
                                                            <div className="h-2 bg-[#EFECE2] rounded-full mt-1 overflow-hidden">
                                                                <div className="h-full rk-gradient-gold" style={{ width: `${(c.revenue / max) * 100}%` }} />
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        }
                                    </Card>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {tab === "orders" && (
                    <div>
                        <h1 className="font-serif text-3xl md:text-4xl mb-6">Orders</h1>
                        <Card>
                            <table className="w-full text-sm">
                                <thead><tr className="text-left text-[11px] uppercase tracking-wider text-[#6C6F7A]">
                                    <th className="pb-3">Order</th><th>Customer</th><th>Items</th><th>Amount</th><th>Status</th><th>Date</th>
                                </tr></thead>
                                <tbody>
                                    {orders.length === 0 ? <tr><td colSpan={6} className="py-8 text-center text-[#6C6F7A]">No orders yet</td></tr> :
                                        orders.map((o) => (
                                            <tr key={o.id} className="border-t border-[#E5E0D5]" data-testid={`admin-order-${o.id}`}>
                                                <td className="py-3 font-mono text-xs">{o.short_id}</td>
                                                <td>+91 {o.user_phone}</td>
                                                <td>{o.items.length}</td>
                                                <td className="font-semibold">{formatINR(o.amount_inr)}</td>
                                                <td><span className={`text-[10px] font-bold px-2 py-1 rounded ${o.status === "paid" ? "bg-[#1D5C42]/10 text-[#1D5C42]" : "bg-[#B88A1A]/10 text-[#B88A1A]"}`}>{o.status}</span></td>
                                                <td className="text-xs text-[#6C6F7A]">{new Date(o.created_at).toLocaleDateString("en-IN")}</td>
                                            </tr>
                                        ))
                                    }
                                </tbody>
                            </table>
                        </Card>
                    </div>
                )}

                {tab === "brands" && (
                    <div>
                        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                            <h1 className="font-serif text-3xl md:text-4xl">Brands <span className="text-rk-muted text-lg font-sans">({brandsTotal})</span></h1>
                            <div className="flex items-center gap-3">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6C6F7A]" />
                                    <input
                                        data-testid="admin-brand-search"
                                        value={query}
                                        onChange={(e) => { setQuery(e.target.value); setBrandsPage(1); }}
                                        placeholder="Search brands"
                                        className="pl-10 pr-4 py-2 border border-[#E5E0D5] rounded-full text-sm w-60"
                                    />
                                </div>
                                <button data-testid="admin-import-btn" onClick={() => setShowImport(true)} className="rk-btn-ghost !py-2 inline-flex items-center gap-2 text-sm">
                                    <Upload className="w-4 h-4" /> Bulk Import
                                </button>
                                <button data-testid="admin-new-brand-btn" onClick={() => setEditBrand({})} className="rk-btn-primary !py-2 inline-flex items-center gap-2 text-sm">
                                    <Plus className="w-4 h-4" /> New Brand
                                </button>
                            </div>
                        </div>
                        <Card>
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                                {brands.map((b) => (
                                    <div key={b.id} className={`border rounded-xl p-3 flex gap-3 ${b.active === false ? "border-rk-border bg-rk-cream/60 opacity-70" : "border-[#E5E0D5]"}`}>
                                        <div className="w-14 h-14 rk-logo-box flex items-center justify-center shrink-0 overflow-hidden">
                                            <BrandLogo brand={b} size="sm" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-[10px] uppercase text-[#6C6F7A]">{b.category}{b.active === false ? " · INACTIVE" : ""}</p>
                                            <p className="font-semibold text-sm truncate">{b.brand_name}</p>
                                            <p className="text-xs text-[#6C6F7A]">₹{b.min_denomination} – ₹{b.max_denomination} · {b.discount_percentage}% off</p>
                                            <div className="flex gap-3 mt-2">
                                                <button data-testid={`edit-brand-${b.id}`} onClick={() => setEditBrand(b)} className="text-xs text-[#0B1437] hover:underline">Edit</button>
                                                <button
                                                    onClick={async () => {
                                                        const r = await adminApi.post(`/admin/brands/${b.id}/toggle-active`);
                                                        setBrands(brands.map(x => x.id === b.id ? { ...x, active: r.data.active } : x));
                                                        toast.success(r.data.active ? "Activated" : "Deactivated");
                                                    }}
                                                    className="text-xs text-rk-gold-deep hover:text-rk-navy inline-flex items-center gap-1"
                                                >
                                                    {b.active === false ? <><Eye className="w-3 h-3" /> Activate</> : <><EyeOff className="w-3 h-3" /> Deactivate</>}
                                                </button>
                                                <button onClick={async () => {
                                                    if (!confirm(`Delete ${b.brand_name}?`)) return;
                                                    await adminApi.delete(`/admin/brands/${b.id}`);
                                                    setBrands(brands.filter(x => x.id !== b.id));
                                                    toast.success("Deleted");
                                                }} className="text-xs text-[#6C6F7A] hover:text-[#0B1437] inline-flex items-center gap-1">
                                                    <Trash2 className="w-3 h-3" /> Delete
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <Pagination page={brandsPage} total={brandsTotal} limit={ADMIN_BRANDS_LIMIT} onChange={setBrandsPage} />
                        </Card>
                        {editBrand && <BrandEditor brand={editBrand} onClose={() => setEditBrand(null)} onSaved={(nb) => {
                            setEditBrand(null);
                            const exists = brands.find(x => x.id === nb.id);
                            if (exists) setBrands(brands.map(x => x.id === nb.id ? nb : x));
                            else setBrands([nb, ...brands]);
                        }} />}
                        {showImport && <BulkImport onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); setBrandsPage(1); setQuery(q => q); }} />}
                    </div>
                )}

                {tab === "users" && (
                    <div>
                        <h1 className="font-serif text-3xl md:text-4xl mb-6">Users ({users.length})</h1>
                        <Card>
                            <table className="w-full text-sm">
                                <thead><tr className="text-left text-[11px] uppercase tracking-wider text-[#6C6F7A]">
                                    <th className="pb-3">Phone</th><th>Name</th><th>Registered</th><th>Last login</th>
                                </tr></thead>
                                <tbody>
                                    {users.length === 0 ? <tr><td colSpan={4} className="py-8 text-center text-[#6C6F7A]">No users yet</td></tr> :
                                        users.map((u) => (
                                            <tr key={u.id} className="border-t border-[#E5E0D5]">
                                                <td className="py-3 font-mono">+91 {u.phone}</td>
                                                <td>{u.name || "—"}</td>
                                                <td className="text-xs text-[#6C6F7A]">{new Date(u.created_at).toLocaleDateString("en-IN")}</td>
                                                <td className="text-xs text-[#6C6F7A]">{u.last_login_at ? new Date(u.last_login_at).toLocaleString("en-IN") : "—"}</td>
                                            </tr>
                                        ))
                                    }
                                </tbody>
                            </table>
                        </Card>
                    </div>
                )}
            </main>
        </div>
    );
}

function Kpi({ label, value, sub, icon: I, accent }) {
    return (
        <div className="bg-white border border-[#E5E0D5] rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase tracking-wider text-[#6C6F7A]">{label}</span>
                <span className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: accent + "15", color: accent }}>
                    <I className="w-4 h-4" />
                </span>
            </div>
            <div className="font-serif text-3xl font-bold text-[#0B1437]">{value}</div>
            {sub && <div className="text-xs text-[#6C6F7A] mt-1">{sub}</div>}
        </div>
    );
}

function Card({ title, children }) {
    return (
        <div className="bg-white border border-[#E5E0D5] rounded-2xl p-5">
            {title && <h3 className="font-serif text-xl mb-4">{title}</h3>}
            {children}
        </div>
    );
}

function EmptyHint() {
    return <p className="text-sm text-[#6C6F7A]">No data yet — place a test order to see insights.</p>;
}

function BrandEditor({ brand, onClose, onSaved }) {
    const existingAcc = (brand.acceptance_type || brand.acceptance || "").toLowerCase();
    const [form, setForm] = useState({
        id: brand.id || null,
        brand_name: brand.brand_name || "",
        category: brand.category || "Online Shopping",
        description: brand.description || "",
        validity: brand.validity || "1 Year",
        acceptance: brand.acceptance || "Online",
        has_online: brand.has_online ?? (existingAcc.includes("online") || existingAcc.includes("both") || existingAcc === ""),
        has_offline: brand.has_offline ?? (existingAcc.includes("offline") || existingAcc.includes("both")),
        min_denomination: brand.min_denomination || 500,
        max_denomination: brand.max_denomination || 10000,
        discount_percentage: brand.discount_percentage ?? 5,
        logo_domain: brand.logo_domain || "",
        hero_image_url: brand.hero_image_url || "",
        custom_logo_url: brand.custom_logo_url || "",
        gallery_urls: Array.isArray(brand.gallery_urls) ? brand.gallery_urls.join("\n") : "",
        is_featured: brand.is_featured || false,
        is_trending: brand.is_trending || false,
    });
    const [categories, setCategories] = useState([]);
    const [showNewCat, setShowNewCat] = useState(false);
    const [newCat, setNewCat] = useState("");

    useEffect(() => {
        adminApi.get("/admin/categories").then(r => setCategories(r.data.items || []));
    }, []);

    const save = async () => {
        try {
            const payload = {
                ...form,
                gallery_urls: (form.gallery_urls || "").split(/[\n,]/).map(s => s.trim()).filter(Boolean),
                hero_image_url: form.hero_image_url || null,
                custom_logo_url: form.custom_logo_url || null,
                acceptance: form.has_online && form.has_offline ? "Online & Offline" : (form.has_online ? "Online" : "Offline"),
            };
            const r = await adminApi.post("/admin/brands/upsert", payload);
            toast.success("Saved");
            onSaved(r.data.brand);
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const addNewCategory = () => {
        const v = newCat.trim();
        if (!v) return;
        if (!categories.includes(v)) setCategories([...categories, v].sort());
        setForm({ ...form, category: v });
        setNewCat("");
        setShowNewCat(false);
    };

    const previewBrand = {
        logo_url: form.custom_logo_url || brand.logo_url,
        custom_logo_url: form.custom_logo_url,
        logo_domain: form.logo_domain,
        brand_name: form.brand_name,
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/55 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-3xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="brand-editor">
                <h3 className="font-serif text-2xl mb-4">{form.id ? "Edit brand" : "New brand"}</h3>
                <div className="space-y-3 text-sm">
                    <Field label="Brand name"><input data-testid="be-name" className="inp" value={form.brand_name} onChange={(e) => setForm({...form, brand_name: e.target.value})} /></Field>

                    {/* Category dropdown + add new */}
                    <Field label="Category">
                        {!showNewCat ? (
                            <div className="flex gap-2">
                                <select
                                    data-testid="be-cat"
                                    className="inp flex-1"
                                    value={form.category}
                                    onChange={(e) => setForm({...form, category: e.target.value})}
                                >
                                    {!categories.includes(form.category) && form.category && (
                                        <option value={form.category}>{form.category}</option>
                                    )}
                                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                                <button type="button" onClick={() => setShowNewCat(true)} data-testid="be-cat-add" className="rk-btn-ghost !py-2 !px-3 text-xs whitespace-nowrap"><Plus className="w-3.5 h-3.5 inline" /> Add new</button>
                            </div>
                        ) : (
                            <div className="flex gap-2">
                                <input autoFocus value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="New category name" className="inp flex-1" />
                                <button type="button" onClick={addNewCategory} className="rk-btn-primary !py-2 !px-3 text-xs">Add</button>
                                <button type="button" onClick={() => setShowNewCat(false)} className="rk-btn-ghost !py-2 !px-3 text-xs">Cancel</button>
                            </div>
                        )}
                    </Field>

                    {/* Brand Image (hero on card top) */}
                    <Field label="Brand Image URL (hero on card top)">
                        <input data-testid="be-hero" className="inp" placeholder="https://cdn.example.com/hero.jpg" value={form.hero_image_url} onChange={(e) => setForm({...form, hero_image_url: e.target.value})} />
                    </Field>

                    {/* Brand Logo (small, bottom-left on card) */}
                    <Field label="Brand Logo URL (bottom-left on card)">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rk-logo-box flex items-center justify-center bg-white border border-rk-border rounded-md overflow-hidden shrink-0">
                                <BrandLogo brand={previewBrand} size="md" />
                            </div>
                            <input data-testid="be-logo" className="inp flex-1" placeholder="https://cdn.example.com/logo.png (leave blank to auto-fetch favicon)" value={form.custom_logo_url} onChange={(e) => setForm({...form, custom_logo_url: e.target.value})} />
                        </div>
                    </Field>

                    {/* Gallery images */}
                    <Field label="Gallery Images (one URL per line — shown as scrollable gallery on PDP)">
                        <textarea data-testid="be-gallery" rows={3} className="inp" placeholder="https://cdn.example.com/img1.jpg&#10;https://cdn.example.com/img2.jpg" value={form.gallery_urls} onChange={(e) => setForm({...form, gallery_urls: e.target.value})} />
                    </Field>

                    <Field label="Logo domain (used only when Logo URL is empty)"><input className="inp" value={form.logo_domain} onChange={(e) => setForm({...form, logo_domain: e.target.value})} /></Field>

                    <div className="grid grid-cols-3 gap-2">
                        <Field label="Min ₹"><input type="number" className="inp" value={form.min_denomination} onChange={(e) => setForm({...form, min_denomination: +e.target.value})} /></Field>
                        <Field label="Max ₹"><input type="number" className="inp" value={form.max_denomination} onChange={(e) => setForm({...form, max_denomination: +e.target.value})} /></Field>
                        <Field label="Discount %"><input type="number" step="0.01" className="inp" value={form.discount_percentage} onChange={(e) => setForm({...form, discount_percentage: +e.target.value})} /></Field>
                    </div>
                    <Field label="Validity"><input className="inp" value={form.validity} onChange={(e) => setForm({...form, validity: e.target.value})} /></Field>

                    {/* Acceptance checkboxes */}
                    <Field label="Acceptance (where can the card be redeemed)">
                        <div className="flex gap-5 pt-1">
                            <label className="flex items-center gap-2 cursor-pointer"><input data-testid="be-online" type="checkbox" checked={form.has_online} onChange={(e) => setForm({...form, has_online: e.target.checked})} /> Online</label>
                            <label className="flex items-center gap-2 cursor-pointer"><input data-testid="be-offline" type="checkbox" checked={form.has_offline} onChange={(e) => setForm({...form, has_offline: e.target.checked})} /> Offline</label>
                        </div>
                    </Field>

                    <Field label="Description"><textarea rows={3} className="inp" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} /></Field>
                    <div className="flex gap-4">
                        <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({...form, is_featured: e.target.checked})} /> Featured</label>
                        <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_trending} onChange={(e) => setForm({...form, is_trending: e.target.checked})} /> Trending</label>
                    </div>
                    <div className="flex gap-3 pt-2">
                        <button onClick={onClose} className="rk-btn-ghost flex-1">Cancel</button>
                        <button data-testid="be-save" onClick={save} className="rk-btn-primary flex-1">Save</button>
                    </div>
                </div>
                <style>{`.inp{width:100%;padding:10px 12px;border:1px solid #E5E0D5;border-radius:10px;outline:none;font-size:14px}`}</style>
            </div>
        </div>
    );
}


function Field({ label, children }) {
    return (
        <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-[#6C6F7A] block mb-1">{label}</label>
            {children}
        </div>
    );
}

function BulkImport({ onClose, onDone }) {
    const [rows, setRows] = useState([]);
    const [headers, setHeaders] = useState([]);
    const [fileName, setFileName] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const parseCSV = (text) => {
        // Minimal CSV parser: supports double-quoted fields with commas.
        const out = [];
        const lines = text.split(/\r?\n/).filter(l => l.trim());
        for (const line of lines) {
            const cells = [];
            let cur = "", inQ = false;
            for (let i = 0; i < line.length; i++) {
                const c = line[i];
                if (inQ) {
                    if (c === '"' && line[i+1] === '"') { cur += '"'; i++; }
                    else if (c === '"') inQ = false;
                    else cur += c;
                } else {
                    if (c === '"') inQ = true;
                    else if (c === ",") { cells.push(cur); cur = ""; }
                    else cur += c;
                }
            }
            cells.push(cur);
            out.push(cells.map(s => s.trim()));
        }
        return out;
    };

    const onFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setFileName(f.name);
        const text = await f.text();
        const parsed = parseCSV(text);
        if (parsed.length < 2) {
            toast.error("CSV needs a header row and at least one data row");
            return;
        }
        const hdr = parsed[0].map(h => h.toLowerCase().replace(/\s+/g, "_"));
        const data = parsed.slice(1).map(row => Object.fromEntries(hdr.map((h, i) => [h, row[i]])));
        setHeaders(hdr);
        setRows(data);
    };

    const upload = async () => {
        setBusy(true);
        try {
            const r = await adminApi.post("/admin/brands/bulk-import", { rows });
            setResult(r.data);
            toast.success(`Imported ${r.data.inserted} new, updated ${r.data.updated}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Import failed");
        } finally {
            setBusy(false);
        }
    };

    const sampleCsv = "brand_name,category,discount_percentage,min_denomination,max_denomination,has_online,has_offline,logo_url,hero_image_url,description\nAcme Store,Apparel,5.5,250,10000,true,false,https://cdn.example.com/acme-logo.png,https://cdn.example.com/acme-hero.jpg,Acme — the perfect gift";

    const downloadSample = () => {
        const blob = new Blob([sampleCsv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "loyltykart_brands_sample.csv"; a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/55 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-3xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="bulk-import">
                <h3 className="font-serif text-2xl mb-2">Bulk Import Brands</h3>
                <p className="text-sm text-rk-muted mb-5">
                    Upload a CSV with columns: <code className="text-xs">brand_name</code>, <code className="text-xs">category</code>, <code className="text-xs">discount_percentage</code>, <code className="text-xs">min_denomination</code>, <code className="text-xs">max_denomination</code>, <code className="text-xs">has_online</code>, <code className="text-xs">has_offline</code>, <code className="text-xs">logo_url</code>, <code className="text-xs">hero_image_url</code>, <code className="text-xs">description</code>. Only <code className="text-xs">brand_name</code> and <code className="text-xs">category</code> are required.
                </p>
                <button type="button" onClick={downloadSample} className="rk-btn-ghost !py-1.5 !px-3 text-xs mb-4">Download sample CSV</button>

                <label className="block border-2 border-dashed border-rk-border rounded-xl p-6 text-center cursor-pointer hover:border-rk-navy">
                    <Upload className="w-6 h-6 mx-auto mb-2 text-rk-gold-deep" />
                    <div className="text-sm font-semibold">{fileName || "Click to choose a CSV file"}</div>
                    {rows.length > 0 && <div className="text-xs text-rk-muted mt-1">{rows.length} rows parsed · headers: {headers.join(", ")}</div>}
                    <input data-testid="bulk-file" type="file" accept=".csv,text/csv" hidden onChange={onFile} />
                </label>

                {result && (
                    <div className="mt-4 bg-rk-cream p-4 rounded-xl text-sm">
                        <div className="font-semibold text-rk-navy">Import result</div>
                        <div className="text-rk-navy/80 mt-1">
                            Inserted: <b>{result.inserted}</b> · Updated: <b>{result.updated}</b> · Skipped: <b>{result.skipped?.length || 0}</b>
                        </div>
                        {result.skipped?.length > 0 && (
                            <ul className="mt-2 text-xs text-rk-muted">
                                {result.skipped.slice(0, 10).map((s, i) => (
                                    <li key={i}>Row {s.row}: {s.reason}</li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}

                <div className="flex gap-3 pt-5">
                    <button onClick={onClose} className="rk-btn-ghost flex-1">Cancel</button>
                    {result ? (
                        <button data-testid="bulk-done" onClick={onDone} className="rk-btn-primary flex-1">Done</button>
                    ) : (
                        <button data-testid="bulk-upload" onClick={upload} disabled={busy || rows.length === 0} className="rk-btn-primary flex-1 disabled:opacity-50">
                            {busy ? "Importing…" : `Import ${rows.length} brand${rows.length === 1 ? "" : "s"}`}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
